import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
    Box, Typography, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    TablePagination, CircularProgress, Chip, Button,
    Dialog, DialogTitle, DialogContent, DialogActions, FormGroup, FormControlLabel, Checkbox, TextField, MenuItem, Alert,
} from '@mui/material';
import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremium';
import { InstitutionService } from '../../services/institution.service';
import { useToast } from '../../context/ToastContext';
import { IInstitution, PlanTier } from '../../interfaces/institution.interface';
import { freeTierLimitMessage, isFreeTierLimitError } from '../../utils/planErrors';
import { ModuleKey } from '../dashboard/registry';
import { CORE_HR_MODULES, FREE_PLAN_MODULES, GATED_MODULE_KEYS, MODULE_META } from '../dashboard/registry/moduleMeta';
import { helpWhy } from '../../help/why';

/**
 * SaaS Operator console: per-institution premium entitlement + gated-module provisioning.
 *
 * Deliberately cross-tenant — the SaaS Operator provisions on behalf of the SaaS product across
 * every institution (the backend restricts the list + write endpoints to SaaSOperator / SystemAdmin).
 * A "Manage" dialog per institution combines both writes (premium PATCH + enabled-modules PUT) into
 * one Save so the two entitlements are never left half-applied.
 */
// The module list is grouped by the cheapest plan that includes each module, so the operator can
// see which switches belong to which subscription. Core HR is a pricing label on a PAID tenant.
const MODULE_SECTIONS: { title: string; caption: string; keys: ModuleKey[] }[] = [
    { title: 'Free plan', caption: 'On by default for every new institution. Also part of Core HR.',
        keys: GATED_MODULE_KEYS.filter(k => FREE_PLAN_MODULES.includes(k)) },
    { title: 'Core HR adds', caption: 'Paid plan. Core HR = the free modules plus these.',
        keys: GATED_MODULE_KEYS.filter(k => CORE_HR_MODULES.includes(k) && !FREE_PLAN_MODULES.includes(k)) },
    { title: 'Paid add-ons', caption: 'Paid plan only, priced per module.',
        keys: GATED_MODULE_KEYS.filter(k => !CORE_HR_MODULES.includes(k)) },
];

export default function TenantProvisioning() {
    const toast = useToast();
    const [rows, setRows] = useState<IInstitution[]>([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [loading, setLoading] = useState(true);

    const [managing, setManaging] = useState<IInstitution | null>(null);
    const [dialogLoading, setDialogLoading] = useState(false);
    const [dialogPremium, setDialogPremium] = useState(false);
    const [dialogModules, setDialogModules] = useState<Set<ModuleKey>>(new Set());
    // granted modules the institution admin switched off; they stay granted (ticked) here
    const [adminOff, setAdminOff] = useState<Set<string>>(new Set());
    const [saving, setSaving] = useState(false);
    // the granted list failed to load: saving would send an empty list and un-grant every module
    const [modulesLoadFailed, setModulesLoadFailed] = useState(false);
    // only the latest Manage open may write the dialog state (a slow earlier load must not overwrite it)
    const openSeq = useRef(0);
    // polish 6: the plan tier (FREE / PAID) is changed here; the saved value is kept to know whether it changed
    const [dialogPlan, setDialogPlan] = useState<PlanTier | ''>('');
    const [savedPlan, setSavedPlan] = useState<PlanTier | ''>('');
    const [limitMessage, setLimitMessage] = useState<string | null>(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const res = await InstitutionService.getInstitutions(page, rowsPerPage);
            setRows(res.content ?? []);
            setTotal(res.totalElements ?? 0);
        } catch (err) {
            toast.showApiError(err, 'Failed to load institutions');
        } finally {
            setLoading(false);
        }
    }, [page, rowsPerPage, toast]);

    useEffect(() => { load(); }, [load]);

    const openManage = async (institution: IInstitution) => {
        const seq = ++openSeq.current;
        const isCurrent = () => seq === openSeq.current;
        setManaging(institution);
        setModulesLoadFailed(false);
        setDialogPremium(institution.premiumDashboardEnabled === true);
        setDialogLoading(true);
        setLimitMessage(null);
        setDialogPlan('');
        setSavedPlan('');
        try {
            try {
                const plan = await InstitutionService.getPlan(institution.id);
                if (!isCurrent()) return;
                setDialogPlan(plan.planTier);
                setSavedPlan(plan.planTier);
            } catch (err) {
                if (!isCurrent()) return;
                toast.showApiError(err, 'Failed to load the plan');
            }
            // the granted list, not the enabled one: a save must not drop a module the admin only switched off
            const access = await InstitutionService.getModuleAccess(institution.id);
            if (!isCurrent()) return;
            setDialogModules(new Set(access.granted.filter((m): m is ModuleKey => (GATED_MODULE_KEYS as string[]).includes(m))));
            setAdminOff(new Set(access.disabled));
        } catch (err) {
            if (!isCurrent()) return;
            toast.showApiError(err, 'Failed to load enabled modules');
            setDialogModules(new Set());
            setAdminOff(new Set());
            setModulesLoadFailed(true);
        } finally {
            if (isCurrent()) setDialogLoading(false);
        }
    };

    const toggleModule = (key: ModuleKey) => {
        setDialogModules(prev => {
            const next = new Set(prev);
            if (next.has(key)) next.delete(key); else next.add(key);
            return next;
        });
    };

    // Changing the plan ticks its defaults: FREE keeps only the free modules (the backend refuses the
    // rest), PAID adds the Core HR set without unticking any add-on the operator already chose.
    const changePlan = (plan: PlanTier) => {
        setDialogPlan(plan);
        setDialogModules(prev => (plan === 'FREE'
            ? new Set(FREE_PLAN_MODULES)
            : new Set([...Array.from(prev), ...CORE_HR_MODULES])));
    };

    const save = async () => {
        if (!managing || modulesLoadFailed) return;
        setSaving(true);
        setLimitMessage(null);
        try {
            // plan first: enabling gated modules on a FREE tenant is refused, so a switch to PAID must land before them
            if (dialogPlan && dialogPlan !== savedPlan) {
                await InstitutionService.updatePlan(managing.id, dialogPlan);
                setSavedPlan(dialogPlan);
            }
            await Promise.all([
                InstitutionService.setPremiumDashboard(managing.id, dialogPremium),
                InstitutionService.setEnabledModules(managing.id, Array.from(dialogModules)),
            ]);
            setRows(prev => prev.map(r => (r.id === managing.id ? { ...r, premiumDashboardEnabled: dialogPremium } : r)));
            toast.showSuccess(`Provisioning saved for ${managing.name}`);
            setManaging(null);
        } catch (err) {
            if (isFreeTierLimitError(err)) {
                setLimitMessage(freeTierLimitMessage(err) ?? 'This module is not part of the free plan.');
            } else {
                toast.showApiError(err, 'Failed to save provisioning');
            }
        } finally {
            setSaving(false);
        }
    };

    return (
        <Box sx={{ p: 3 }} data-help-page="platform.tenants.page">
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <WorkspacePremiumIcon color="primary" />
                <Typography variant="h5">Tenant Provisioning</Typography>
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Unlock the business-owner dashboard and choose which modules are enabled, per institution.
            </Typography>

            <Paper>
                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>
                ) : (
                    <>
                        <TableContainer data-help-section="platform.tenants.table">
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell data-help="platform.tenants.col.institution">Institution</TableCell>
                                        <TableCell data-help="platform.tenants.col.code">Code</TableCell>
                                        <TableCell align="center" data-help="platform.tenants.col.premium">Premium dashboard</TableCell>
                                        <TableCell align="center" data-help="platform.tenants.col.actions">Actions</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {rows.length === 0 ? (
                                        <TableRow>
                                            <TableCell colSpan={4} align="center">No institutions found.</TableCell>
                                        </TableRow>
                                    ) : rows.map(inst => (
                                        <TableRow key={inst.id} hover>
                                            <TableCell>{inst.name}</TableCell>
                                            <TableCell>{inst.code}</TableCell>
                                            <TableCell align="center">
                                                {inst.premiumDashboardEnabled
                                                    ? <Chip size="small" color="success" label="Enabled" />
                                                    : <Chip size="small" variant="outlined" label="Disabled" />}
                                            </TableCell>
                                            <TableCell align="center">
                                                <Button size="small" onClick={() => openManage(inst)} data-help="platform.tenants.action.manage">Manage</Button>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                        <TablePagination
                            component="div"
                            data-help="platform.tenants.paging"
                            count={total}
                            page={page}
                            onPageChange={(_, p) => setPage(p)}
                            rowsPerPage={rowsPerPage}
                            onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
                            rowsPerPageOptions={[10, 25, 50]}
                        />
                    </>
                )}
            </Paper>

            <Dialog open={managing !== null} onClose={() => setManaging(null)} fullWidth maxWidth="xs" closeAfterTransition={false} data-help-page="platform.tenants.manage.page">
                <DialogTitle>Manage {managing?.name}</DialogTitle>
                <DialogContent dividers>
                    {dialogLoading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}><CircularProgress size={24} /></Box>
                    ) : (
                        <FormGroup>
                            {modulesLoadFailed && (
                                <Alert severity="error" sx={{ mb: 2 }}>
                                    The current modules could not be loaded, so saving is turned off. Close and open Manage again.
                                </Alert>
                            )}
                            {limitMessage && (
                                <Alert severity="warning" sx={{ mb: 2 }}
                                    action={dialogPlan !== 'PAID' ? <Button color="inherit" size="small" data-help="platform.tenants.manage.changeToPaid" onClick={() => { changePlan('PAID'); setLimitMessage(null); }}>Change plan to PAID</Button> : undefined}>
                                    {limitMessage} Change the plan below, then save again.
                                </Alert>
                            )}
                            <TextField select size="small" label="Plan" data-help="platform.tenants.manage.field.plan" value={dialogPlan} disabled={dialogPlan === '' && savedPlan === ''}
                                onChange={(e) => changePlan(e.target.value as PlanTier)} sx={{ mb: 2 }}
                                helperText="FREE is limited to 50 employees and the basic modules; PAID lifts both limits.">
                                <MenuItem value="FREE">Free</MenuItem>
                                <MenuItem value="PAID">Paid</MenuItem>
                            </TextField>
                            <FormControlLabel
                                data-help="platform.tenants.manage.field.premium"
                                control={<Checkbox checked={dialogPremium} onChange={(e) => setDialogPremium(e.target.checked)} />}
                                label="Premium dashboard"
                            />
                            <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>Enabled modules</Typography>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1 }}>
                                <Button size="small" variant="outlined" data-help="platform.tenants.manage.tickFree"
                                    onClick={() => setDialogModules(new Set(FREE_PLAN_MODULES))}>Tick Free defaults</Button>
                                <Button size="small" variant="outlined" data-help="platform.tenants.manage.tickCoreHr"
                                    onClick={() => setDialogModules(new Set(CORE_HR_MODULES))}>Tick Core HR defaults</Button>
                            </Box>
                            {MODULE_SECTIONS.map(section => (
                                <Box key={section.title} sx={{ mt: 1.5 }}>
                                    <Typography variant="overline" component="h3" sx={{ display: 'block', lineHeight: 1.6 }}>{section.title}</Typography>
                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>{section.caption}</Typography>
                                    {section.keys.map(key => (
                                        <Box key={key} sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', columnGap: 1 }}>
                                            <FormControlLabel
                                                data-help="platform.tenants.manage.field.module"
                                                control={<Checkbox checked={dialogModules.has(key)} onChange={() => toggleModule(key)} />}
                                                label={MODULE_META[key].label}
                                            />
                                            {FREE_PLAN_MODULES.includes(key) && <Chip size="small" color="success" variant="outlined" label="Free" />}
                                            {CORE_HR_MODULES.includes(key) && <Chip size="small" color="primary" variant="outlined" label="Core HR" />}
                                            {dialogModules.has(key) && adminOff.has(key) && (
                                                <Chip data-help="platform.tenants.manage.offByInstitution" size="small" color="warning" variant="outlined" label="Off by institution" />
                                            )}
                                        </Box>
                                    ))}
                                </Box>
                            ))}
                        </FormGroup>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setManaging(null)} disabled={saving} data-help="platform.tenants.manage.cancel">Cancel</Button>
                    <Button onClick={save} disabled={saving || dialogLoading || modulesLoadFailed} variant="contained" data-help="platform.tenants.manage.save" {...helpWhy(!saving && dialogLoading ? { kind: 'status', message: 'The current plan and modules are still loading. Wait a moment.' } : !saving && modulesLoadFailed ? { kind: 'status', message: 'The current modules could not be loaded. Close and open Manage again before saving.' } : null)}>Save</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
